"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useMemo } from "react";
import { Calendar, ChevronLeft, ChevronRight, User, Funnel as Filter } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
function BlogPostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const categories = useMemo(() => {
    const cats = new Map<number, string>();
    posts.forEach(post => {
      post.categories?.forEach(cat => {
        cats.set(cat.id, cat.name);
      });
    });
    return Array.from(cats.entries()).map(([id, name]) => ({
      id,
      name
    }));
  }, [posts]);
  const filteredPosts = useMemo(() => {
    if (!selectedCategory) return posts;
    return posts.filter(post => post.categories?.some(cat => cat.name === selectedCategory));
  }, [posts, selectedCategory]);
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="flex h-full flex-col overflow-hidden">
            <Skeleton className="h-48 w-full rounded-lg" />
            <CardHeader className="space-y-3">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </CardHeader>
            <CardFooter className="mt-auto border-t pt-4">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  <Skeleton className="size-6 rounded-lg" />
                  <Skeleton className="h-3 w-16" />
                </div>
                <Skeleton className="h-3 w-20" />
              </div>
            </CardFooter>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="mb-4 text-muted-foreground">Failed to load posts.</p>
        <Button onClick={refetch} variant="outline">
          Try Again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="py-12 text-center">
        <p className="text-lg text-muted-foreground">No posts found.</p>
      </div>;
  }
  const formatExcerpt = (html?: string) => {
    if (!html) return "";
    const stripped = html.replace(/(<([^>]+)>)/gi, "");
    return stripped.length > 150 ? stripped.substring(0, 150) + "..." : stripped;
  };
  return <div className="space-y-12">
      {}
      <div className="flex flex-col items-center gap-4 md:flex-row md:justify-center">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Filter className="size-4" />
          <span>Filter by:</span>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          <Badge variant={selectedCategory === null ? "default" : "outline"} className={cn("cursor-pointer px-4 py-1.5 text-sm transition-all hover:bg-primary hover:text-primary-foreground", selectedCategory === null && "shadow-md")} onClick={() => setSelectedCategory(null)}>
            All Categories
          </Badge>
          {categories.map((cat, i) => <Badge key={cat.id} data-index={i} variant={selectedCategory === cat.name ? "default" : "outline"} className={cn("cursor-pointer px-4 py-1.5 text-sm transition-all hover:bg-primary hover:text-primary-foreground", selectedCategory === cat.name && "shadow-md")} onClick={() => setSelectedCategory(cat.name)}>
              {cat.name}
            </Badge>)}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredPosts.map((post, i) => <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden transition-shadow duration-300 hover:shadow-md">
            {post.featuredImage && <Link to={post.link || "#"} className="block overflow-hidden">
                <AspectRatio ratio={16 / 9}>
                  <img src={post.featuredImage} alt={post.title} loading="lazy" className="size-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </AspectRatio>
              </Link>}
            <CardHeader className="flex-1 space-y-3">
              {post.categories && post.categories.length > 0 && <div className="flex flex-wrap gap-2">
                  {post.categories.slice(0, 2).map((cat, j) => <Badge key={cat.id} data-index={j} variant="secondary" className="font-normal">
                      {cat.name}
                    </Badge>)}
                </div>}
              <Link to={post.link || "#"} className="block">
                <CardTitle className="font-default text-xl font-semibold leading-tight transition-colors group-hover:text-primary">
                  {post.title}
                </CardTitle>
              </Link>
              {post.excerpt && <CardDescription className="text-sm text-muted-foreground">
                  {formatExcerpt(post.excerpt)}
                </CardDescription>}
            </CardHeader>
            <CardFooter className="mt-auto border-t pt-4 text-sm text-muted-foreground">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  <SafeHydrate><Avatar className="size-6">
                    {post.author?.avatar?.["24"] && <AvatarImage src={post.author.avatar["24"]} alt={post.author.name} />}
                    <AvatarFallback>
                      <User className="size-3" />
                    </AvatarFallback>
                  </Avatar></SafeHydrate>
                  <span className="font-medium">{post.author?.name || "Admin"}</span>
                </div>
                {post.date && <div className="flex items-center gap-1.5">
                    <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                    <span>
                      {new Date(post.date).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric"
                })}
                    </span>
                  </div>}
              </div>
            </CardFooter>
          </Card>)}
      </div>

      {filteredPosts.length === 0 && selectedCategory && <div className="py-12 text-center">
          <p className="text-muted-foreground">No posts found in this category on the current page.</p>
          <Button variant="link" onClick={() => setSelectedCategory(null)} className="mt-2">
            Clear filter
          </Button>
        </div>}

      {(hasNext || hasPrev) && <Pagination>
          <PaginationContent>
            <PaginationItem>
              <Button variant="ghost" onClick={prevPage} disabled={!hasPrev} className="gap-1 pl-2.5">
                <ChevronLeft className="size-4" />
                <span className="hidden sm:inline-block">Previous</span>
              </Button>
            </PaginationItem>
            <PaginationItem>
              <span className="px-4 text-sm font-medium text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
            </PaginationItem>
            <PaginationItem>
              <Button variant="ghost" onClick={nextPage} disabled={!hasNext} className="gap-1 pr-2.5">
                <span className="hidden sm:inline-block">Next</span>
                <ChevronRight className="size-4" />
              </Button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>}
    </div>;
}
export default function BlogPostsGrid() {
  return <section id="blog-posts-grid" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center">
          <h2 className="font-default text-3xl font-bold tracking-tight md:text-4xl">
            Latest Articles
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Discover our latest news, tips, and insights.
          </p>
        </div>
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 12
      }}>
          <BlogPostsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}